import { createMutation } from '@/mutations'
import {
  addWardrobeFromCatalog,
  batchUnlinkWardrobe,
  deleteWardrobeItem,
  patchWardrobeItem,
  uploadWardrobe,
  uploadWardrobeImage,
  type WardrobeBatchUnlinkResponse,
  type WardrobeDeleteResponse,
  type WardrobeFromCatalogResponse,
  type WardrobePatchBody,
  type WardrobePatchResponse,
  type WardrobeUploadResponse,
} from '@/features/style-space/api'
import { styleSpaceQueriesOf } from '@/queries/style-space'

/** A wardrobe write reaches every look and capsule that wears the piece. */
const invalidateWardrobeReaders = styleSpaceQueriesOf(
  'wardrobe',
  'wardrobe-item',
  'outfits',
  'capsules',
  'capsule',
  'available-items',
)

export interface UploadWardrobePhotosVariables {
  files: File[]
  /** Keys already uploaded in a previous attempt — detection is retried without re-uploading. */
  uploadedKeys?: string[]
}

export type UploadWardrobeStage = 'upload' | 'detect'

/** Which hop failed, so the dialog can say "photo 3 did not upload" vs "detection failed". */
export class UploadWardrobeError extends Error {
  readonly stage: UploadWardrobeStage
  readonly statusCode: number | undefined
  readonly detail: string | undefined
  /** Keys that did reach S3 before the failure — reusable for a retry. */
  readonly uploadedKeys: string[]
  readonly fileIndex: number | undefined

  constructor(input: {
    stage: UploadWardrobeStage
    cause: unknown
    uploadedKeys: string[]
    fileIndex?: number
  }) {
    const axiosLike = input.cause as {
      response?: { status?: number; data?: { detail?: unknown; message?: string } }
      message?: string
    } | null
    const detail = axiosLike?.response?.data?.detail
    super(
      typeof detail === 'string'
        ? detail
        : axiosLike?.response?.data?.message ?? axiosLike?.message ?? `${input.stage} failed`,
    )
    this.name = 'UploadWardrobeError'
    this.stage = input.stage
    this.statusCode = axiosLike?.response?.status
    this.detail = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : undefined
    this.uploadedKeys = input.uploadedKeys
    this.fileIndex = input.fileIndex
  }
}

const UPLOAD_CONCURRENCY = 3

/**
 * Detection runs in the request path — decode, detect, crop, S3 per photo, all
 * before the answer (§1.3). fair-api gives the ML batch call 30 s total. Measured
 * 2026-09-11 against proxy/8000/process-batch: ~7 s per photo plus ~4 s per call
 * when idle (1 → 12 s, 2 → 19 s, 4 → 31 s), but two photos took 30–41 s once the
 * host was busy, so only one per call fits the timeout reliably. Calls are
 * sequential on purpose — parallel batches slow each other down on the same
 * model. `processed_items` stays 1:1 with the keys sent, so the parts
 * concatenate back into photo order. The real fix is server-side (longer
 * timeout or the queued ML version); until then this is the safe size.
 */
const DETECT_BATCH = 1

function mergeDetections(parts: WardrobeUploadResponse[]): WardrobeUploadResponse {
  return {
    processedItems: parts.flatMap((part) => part.processedItems ?? []),
    failedItems: parts.flatMap((part) => part.failedItems ?? []),
  }
}

/**
 * Two hops by contract: each photo → S3 key (`POST /images/upload_img`, three
 * at a time), then ONE detection call with every key (`POST /playground/user-wardrobe`,
 * synchronous, 1..10 keys). A failure names its hop and keeps the keys that made it.
 */
export const useUploadWardrobePhotosMutation = () =>
  createMutation<WardrobeUploadResponse, UploadWardrobePhotosVariables>({
    mutationFn: async ({ files, uploadedKeys }) => {
      const keys: string[] = uploadedKeys ? [...uploadedKeys] : new Array<string>(files.length)
      if (!uploadedKeys) {
        let next = 0
        const worker = async () => {
          while (next < files.length) {
            const index = next
            next += 1
            try {
              const uploaded = await uploadWardrobeImage(files[index] as File)
              keys[index] = uploaded.data.img_url
            } catch (cause) {
              throw new UploadWardrobeError({
                stage: 'upload',
                cause,
                uploadedKeys: keys.filter(Boolean),
                fileIndex: index,
              })
            }
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, () => worker()),
        )
      }
      const parts: WardrobeUploadResponse[] = []
      for (let start = 0; start < keys.length; start += DETECT_BATCH) {
        try {
          const response = await uploadWardrobe(keys.slice(start, start + DETECT_BATCH))
          parts.push(response.data)
        } catch (cause) {
          throw new UploadWardrobeError({ stage: 'detect', cause, uploadedKeys: keys })
        }
      }
      return mergeDetections(parts)
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export interface PatchWardrobeItemVariables {
  overviewId: number
  body: WardrobePatchBody
}

export const usePatchWardrobeItemMutation = () =>
  createMutation<WardrobePatchResponse, PatchWardrobeItemVariables>({
    mutationFn: async ({ overviewId, body }) => {
      const response = await patchWardrobeItem(overviewId, body)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useDeleteWardrobeItemMutation = () =>
  createMutation<WardrobeDeleteResponse, { overviewId: number }>({
    mutationFn: async ({ overviewId }) => {
      const response = await deleteWardrobeItem(overviewId)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useBatchUnlinkWardrobeMutation = () =>
  createMutation<WardrobeBatchUnlinkResponse, { overviewIds: number[] }>({
    mutationFn: async ({ overviewIds }) => {
      const response = await batchUnlinkWardrobe(overviewIds)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useAddWardrobeFromCatalogMutation = () =>
  createMutation<WardrobeFromCatalogResponse, { productId: number; variantId?: number | null }>({
    mutationFn: async ({ productId, variantId }) => {
      const response = await addWardrobeFromCatalog(productId, variantId)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })
